import { action, computed, extendObservable, observable } from "mobx";
import { IListProvider } from "shared/_common/list/IListProvider";
import { LoadingStateMdl } from "shared/_common/loaders/_models/LoadingStateMdl";
import { createLoadingStateFromPromise } from "shared/_common/loaders/loadingStateUtils";
import { ObservableArray } from "mobx/lib/types/observablearray";
import { TFilter } from "admin/_common/filters/TFilter";
import _ from "lodash";
import { TObjWithId } from "_common/types/GenericTypes";

export const DEFAULT_PAGE_SIZE = 20;
export class ListStore<T extends TObjWithId> {
    @observable items: (T | undefined)[] = [];
    @observable count: number | undefined;
    @observable selectedPage: number;
    @observable pageSize: number;
    @observable pageSizeList: number[] = [2, 5, 10, 20, 50];
    @observable sort: { [key: string]: number } | undefined;
    @observable filters: TFilter[] = [];

    readonly listId: string;
    @observable private loadingStates: {
        [page: number]: LoadingStateMdl<{ count: number; items: T[] }>;
    } = {};
    private readonly listProvider: IListProvider<T>;
    private resetAndReload = _.debounce(
        action(() => {
            this.reset();
            this.setSelectedPage(1);
        }),
        1000,
    );

    constructor(
        listId: string,
        listProvider: IListProvider<T>,
        initialState?: { count: number; pages: { [offset: number]: T[] } },
        pageSize?: number,
        noInitialLoading?: boolean,
        initialFilters?: TFilter[],
        initialSort?: { [key: string]: number },
    ) {
        this.listId = listId;
        this.listProvider = listProvider;
        this.selectedPage = 1;
        this.pageSize = pageSize ?? DEFAULT_PAGE_SIZE;

        if (initialState) this.init(initialState);
        if (initialFilters && initialFilters.length > 0) this.filters = initialFilters;
        if (initialSort) this.sort = initialSort;
        if (!noInitialLoading) this.load();
    }

    @computed get paginatedItems() {
        const offset = (this.selectedPage - 1) * this.pageSize;
        return this.items.slice(offset, offset + this.pageSize);
    }

    @computed get currentLoadingState() {
        return this.loadingStates[this.selectedPage];
    }

    @computed get hasMorePage() {
        return this.count === undefined || this.items.length < this.count;
    }

    @action load(page: number = this.selectedPage) {
        if (!this.loadingStates[page] || this.loadingStates[page].status === "IDLE") {
            const offset = (page - 1) * this.pageSize;
            if (this.items[offset]) {
                extendObservable(this.loadingStates, {
                    [page]: new LoadingStateMdl("SUCCEEDED"),
                }); //TODO set value in loading state
            } else {
                const loadPromise = this.listProvider
                    .list(offset, this.pageSize, this.listId, this.sort, this.filters)
                    .then(
                        action((result) => {
                            const { count, items } = result;
                            this.count = count;
                            if (offset > this.items.length) {
                                this.items.push(...new Array(offset - this.items.length).fill(undefined));
                            }
                            for (let i = 0; i < items.length; i++) {
                                this.items[offset + i] = items[i];
                            }
                            this.loadingStates[page].setSuccess(result);
                            return result;
                        }),
                        (err) => this.loadingStates[page].setError(err),
                    );
                if (!this.loadingStates[page]) {
                    extendObservable(this.loadingStates, {
                        [page]: createLoadingStateFromPromise(loadPromise),
                    });
                } else {
                    this.loadingStates[page].startLoading(loadPromise as any);
                }
            }
        }

        return this.loadingStates[page];
    }

    getLoadingState(page: number) {
        if (!this.loadingStates[page]) {
            this.loadingStates[page] = new LoadingStateMdl<{ count: number; items: T[] }>();
        }
        return this.loadingStates[page];
    }

    getSync(itemId: string) {
        return this.items.find((item) => (item as any)?._id === itemId);
    }

    @action setPageSize(pageSize: number) {
        this.pageSize = pageSize;
        this.resetAndReload();
    }

    @action setSelectedPage(page: number) {
        this.selectedPage = page;
        return this.load(page);
    }

    @action setSort(sort: { [key: string]: number }) {
        this.sort = sort;
        this.resetAndReload();
    }

    @action updateFilter(filter: TFilter, index?: number) {
        if (index === undefined) this.filters.push(filter);
        else this.filters.splice(index, 1, filter);
        this.resetAndReload();
    }

    @action removeFilter(index: number) {
        this.filters.splice(index, 1);
        this.resetAndReload();
    }

    @action reset() {
        this.loadingStates = {};
        ((this.items as unknown) as ObservableArray<void>).clear();
    }

    @action onCreate(item: T) {
        if (this.items.find((listItem) => listItem?._id === item._id)) {
            this.onUpdate(item);
            return;
        }
        this.items.unshift(item);
    }

    @action onUpdate(updatedItem: T) {
        this.items.splice(
            this.items.findIndex((item) => item?._id === updatedItem._id),
            1,
            updatedItem,
        );
    }

    @action onDelete(itemId: string) {
        this.items.splice(
            this.items.findIndex((item) => item?._id === itemId),
            1,
        );
    }

    reload() {
        this.reset();
        this.load();
    }

    private init(initialState: { count: number; pages: { [offset: number]: T[] } }) {
        this.count = initialState.count;
        Object.keys(initialState.pages)
            .map(Number)
            .forEach((offset) => {
                const items = initialState.pages[offset];
                this.items.splice(offset, items.length, ...items);
            });
    }
}
